import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import isEmpty from 'lodash/isEmpty'
import { useParams } from 'react-router-dom'
import { useStatsigClient } from '@statsig/react-bindings'
import { ResponsiveLayout } from 'containers'
import { Wrapper, ErrorWrapper } from './ReleaseInformation.styles'
import { thunks as ReleaseThunks } from 'reducers/Releases'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import { ReleasesService } from 'services'
import { Tonality } from '@dsny/dsny-component-library'
import { RELEASE_INFO_ROW_LAYOUT } from 'constants/featureFlags'
import ReleaseInformationLegacyLayout from './layouts/LegacyLayout'
import ReleaseInformationColumnLayout from './layouts/ColumnLayout'
import ReleaseInformationWithRollout from './ReleaseInformationWithRollout'
import useReleaseInformationResponsiveBreakpointsV2 from './hooks/useReleaseInformationResponsiveBreakpointsV2'

const ReleaseInformationError = ({ error, layoutType }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()

  return (
    <Wrapper
      $responsiveBreakpointsV2={responsiveBreakpointsV2}
      data-layout={layoutType}
    >
      {error === 'RELEASE_NOT_FOUND' && (
        <ErrorWrapper>
          <Tonality.UIHeading
            headingStyle="headline4"
            style={{ marginBottom: 12, display: 'flex' }}
            color={theme.colors.neutralW90}
          >
            {t('release-info.no-access-to-release')}
          </Tonality.UIHeading>

          <Tonality.UIText textStyle="body" color={theme.colors.neutralW70}>
            {t('release-info.go-back-to-your')}{' '}
            <Link style={{ textDecoration: 'underline' }} to="/releases">
              {t('release-list.releases')}
            </Link>
          </Tonality.UIText>
        </ErrorWrapper>
      )}
    </Wrapper>
  )
}

ReleaseInformationError.propTypes = {
  error: PropTypes.string.isRequired,
  layoutType: PropTypes.oneOf(['column', 'row']).isRequired
}

export default function ReleaseInformation() {
  const dispatch = useDispatch()
  const params = useParams()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const [release, setRelease] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const isNewReleaseInfoLayoutEnabled =
    !isStatsigLoading && checkGate(RELEASE_INFO_ROW_LAYOUT)

  useEffect(() => {
    const getReleaseDetails = async () => {
      setLoading(true)
      setError('')

      try {
        await dispatch(
          ReleaseThunks.getDetail(params.id, (data) => {
            setLoading(false)
            setRelease(data)
          })
        )
      } catch (error) {
        console.log('Error fetching release details:', error.status)
        if (error.status === 404) {
          setError('RELEASE_NOT_FOUND')
        }
        if (error.status === 403) {
          setError('FORBIDDEN')
        }

        if (error.status === 410) {
          setError('RELEASE_NOT_AVAILABLE')
        }

        setLoading(false)
      }
    }

    getReleaseDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id])

  const handleDownload = async (release, format, sourceLabel) => {
    const releaseDetail = await ReleasesService.getDetail(release.releaseid)

    if (!isEmpty(releaseDetail.data)) {
      dispatch(
        DownloadsThunks.addReleaseToDownloadQueue(
          release,
          format,
          sourceLabel || 'release_information'
        )
      )
    }
  }

  const LayoutComponent = isNewReleaseInfoLayoutEnabled
    ? ReleaseInformationColumnLayout
    : ReleaseInformationLegacyLayout
  const contentContainerStyles = {
    overflow: error || !isNewReleaseInfoLayoutEnabled ? 'hidden' : 'auto'
  }

  const content = error ? (
    <ReleaseInformationError
      error={error}
      layoutType={isNewReleaseInfoLayoutEnabled ? 'column' : 'row'}
    />
  ) : (
    <LayoutComponent
      release={release}
      setRelease={setRelease}
      loading={loading}
      handleDownload={handleDownload}
    />
  )

  return (
    <ResponsiveLayout
      content={
        <ReleaseInformationWithRollout>{content}</ReleaseInformationWithRollout>
      }
      contentContainerStyles={contentContainerStyles}
    />
  )
}
